import React from 'react';
import classnames from 'classnames';
import { useRouter } from 'found';

import {
  List,
  Grid,
  Drawer,
  Collapse,
  ListItem as MuiListItem,
  Typography,
  Chip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, Translate } from '@hydra/common/components';
import { useFeatureFlags } from '@hydra/common/components/FeatureFlags/featureFlagContext';
import TrialDaysLeftCta from '@hydra/routes/trial-ended/components/TrialDaysLeftCta';

import { useI18n } from '@hydra/common/customHooks';

import { getActiveCrawlerCategory } from '@hydra/utils/parseRouteParams';
import { getHostname } from '@hydra/utils/urlUtil';
import { getSearchParams } from '@hydra/utils/searchParams';
import { getListItemsObject } from '@hydra/utils/navigationUtil';

import { ItemProps, VisibleItem } from '@hydra/types/navigation';
import { DudaProject } from '@hydra/types';

const LIST_WIDTH = 234;

const useStyles = makeStyles(theme => ({
  listRoot: {
    position: 'fixed',
    width: LIST_WIDTH,
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    height: '100vh',
    overflowY: 'auto'
  },
  listContainer: {
    minHeight: `calc(100vh - ${theme.mixins.toolbar.minHeight}px)`
  },
  listContainerWithoutHeader: {
    minHeight: '100vh'
  },
  hiddenXs: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'flex'
    }
  },
  hiddenMd: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex'
    }
  },
  visibleMd: {
    display: 'flex',
    [theme.breakpoints.up('md')]: {
      display: 'none'
    }
  },
  item: {
    textDecoration: 'none',
    borderRadius: theme.shape.borderRadius * 4,
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(0.5),
    transition: 'background .3s cubic-bezier(.23,1,.32,1) 0ms',
    '&:hover, &:focus, &:active': { textDecoration: 'none !important' },
    '&:hover': {
      backgroundColor: theme.palette.common.white,
      '& $secondaryIconContainer': {
        opacity: 1
      }
    }
  },
  itemHeader: {
    borderRadius: 0,
    fontWeight: theme.typography.fontWeightSemibold,
    height: theme.mixins.toolbar.minHeight,
    marginBottom: theme.spacing(2),
    borderColor: theme.palette.grey[300]
  },
  icon: {
    width: 20,
    height: 20,
    marginRight: theme.spacing(1)
  },
  itemText: {
    fontSize: theme.typography.fontSizeSmall,
    fontWeight: theme.typography.fontWeightSemibold
  },
  itemActive: {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.common.white,
    '& > svg': {
      fill: theme.palette.primary.main
    }
  },
  divider: { margin: theme.spacing(1, 0, 2) },
  iconHolder: {
    display: 'flex',
    position: 'relative',
    marginRight: theme.spacing(2),
    width: 20,
    height: 20
  },
  headerText: {
    overflowY: 'scroll',
    whiteSpace: 'nowrap',
    scrollbarWidth: 'none', // Firefox
    '&::-webkit-scrollbar': {
      display: 'none' // Safari + Chrome
    }
  },
  nested: { paddingLeft: theme.spacing(5) },
  chevronContainer: {
    display: 'flex',
    marginLeft: 'auto'
  },
  secondaryIconContainer: {
    display: 'flex',
    marginLeft: 'auto',
    opacity: 0
  },
  drawer: {
    height: `calc(100vh - ${theme.mixins.toolbar.minHeight}px)`,
    width: LIST_WIDTH,
    marginTop: theme.mixins.toolbar.minHeight,
    marginLeft: theme.mixins.toolbar.minHeight,
    zIndex: theme.zIndex.appBar - 2,
    borderRight: 'none'
  },
  chip: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.fontSizeTiny,
    pointerEvents: 'none',
    height: theme.spacing(2.75),
    marginLeft: theme.spacing(0.75)
  },
  footer: {
    position: 'absolute',
    bottom: theme.spacing(2)
  },
  trialBannerWrapper: {
    padding: theme.spacing(2, 1.5)
  }
}));

export interface SecondaryNavigationProps {
  active: string
  open: boolean
  onClose: () => void
  children?: React.ReactNode
  visibleItems: VisibleItem[]
  hasHeader?: boolean
  dudaProject?: DudaProject | null
  favicon: string | null
  showFooter?: boolean
}

export default function SecondaryNavigation ({
  active = '',
  onClose = () => { },
  open = false,
  visibleItems,
  hasHeader = false,
  dudaProject = null,
  favicon,
  showFooter = false
}: SecondaryNavigationProps) {
  const featureFlags = useFeatureFlags();
  const allowSubFolder = featureFlags && featureFlags.subfolder;
  const classes = useStyles();
  const { locale } = useI18n();
  const projectSearchParam = getSearchParams('project');
  const { match: { params, routes } } = useRouter();
  const { url } = params;
  const hostname = getHostname(url);
  const projectUrl = allowSubFolder ? projectSearchParam || url : hostname;
  const activeCategory = getActiveCrawlerCategory(routes);
  const sidenavItems = getListItemsObject(active, activeCategory, locale, projectUrl, visibleItems);

  const faviconSrc = favicon || `https://www.google.com/s2/favicons?sz=32&domain=${projectUrl}`;
  const list = (
    <div className={classes.listRoot}>
      <List component='nav' disablePadding className={hasHeader ? classes.listContainer : classes.listContainerWithoutHeader}>
        <MuiListItem
          key={1}
          divider
          button
          component='a'
          href={`/${locale}/dashboard/${encodeURIComponent(projectUrl)}`}
          className={classnames(
            classes.item,
            classes.itemHeader
          )}
        >
          <div className={classes.iconHolder}>
            <img className={classes.icon} src={faviconSrc} />
          </div>
          <span className={classnames(classes.headerText)}>
            {projectUrl}
          </span>
        </MuiListItem>
        {
          sidenavItems.map((item, index) => <ListItem item={item} key={index} activeCategory={activeCategory} />)
        }
        <div className={classes.footer}>
          {dudaProject && dudaProject.isPlanTrial && (
            <div className={classes.trialBannerWrapper}>
              <TrialDaysLeftCta days={dudaProject.daysInTrial} upgradeDetails={dudaProject.upgradeDetails} componentType='div' />
            </div>
          )}
          {
            showFooter && (
              <MuiListItem
                button
                component='a'
                href={`/${locale}/integrations/duda/getting-started`}
                target='_blank'
                className={classes.item}
              >
                <Icon className={classnames(classes.icon)} type='information' />
                <Typography className={classes.itemText}>
                  <Translate content='nav_getting_started' />
                </Typography>
                <div className={classnames(classes.secondaryIconContainer)}><Icon className={classnames(classes.icon)} color='primary' type='external-link' /></div>
              </MuiListItem>
            )
          }
        </div>
      </List>
    </div>
  );

  return (
    <>
      <Drawer
        PaperProps={{
          classes: {
            root: classes.drawer
          },
          elevation: 0
        }}
        variant='persistent'
        className={classes.visibleMd}
        open={open}
        onClose={onClose}
      >
        {list}
      </Drawer>
      <Grid item sm={3} className={classnames(classes.hiddenMd)}>
        {list}
      </Grid>
    </>
  );
}

interface ListItemProps {
  item: ItemProps
  className?: string | undefined,
  activeCategory?: string | undefined
}

const ListItem: React.FC<ListItemProps> = React.memo(({ item, className: classNameProps, activeCategory }) => {
  const classes = useStyles();
  const { translate } = useI18n();
  const [openNest, setOpenNest] = React.useState(Boolean(activeCategory));
  const handleClick = () => {
    setOpenNest(!openNest);
  };
  const MuiListItemProps = item.subitems ? { onClick: handleClick } : { href: item.href };
  const chevronIcon = openNest ? <Icon type='chevron-up' /> : <Icon type='chevron-down' />;
  return (
    <>
      <MuiListItem
        button
        component='a'
        className={classnames(
          classes.item,
          [
            item.active && classes.itemActive
          ],
          classNameProps
        )}
        disabled={Boolean(item.disabled)}
        {...MuiListItemProps}
      >
        {
          item.icon && <Icon className={classnames(classes.icon)} type={item.icon} />
        }
        <Typography className={classes.itemText}>
          <Translate content={item.label} />
          {
            item.hasNewBadge && (
              <Chip
                color='primary'
                component='span'
                className={classes.chip}
                label={translate('navigation_new_badge_text')}
              />
            )
          }
        </Typography>
        <div className={classes.chevronContainer}>
          {
            item.subitems && chevronIcon
          }
        </div>
      </MuiListItem>
      {
        item.subitems && (
          <Collapse in={openNest} unmountOnExit>
            {
              item.subitems.map((i, k) => (<ListItem item={i} key={k} className={classes.nested} />))
            }
          </Collapse>
        )
      }
    </>
  );
});
